import { z } from 'zod';

export const TIME_RANGE_VALUES = ['1', '3', '6', '12'] as const;
export type UsageBreakdownTimeRange = (typeof TIME_RANGE_VALUES)[number];

/** Stands in for "no contract covered this period" inside the contracts filter. */
export const PAYG_CONTRACT_FILTER_ID = 'payg';

export type UsageBreakdownFilterValues = {
  clusters: string[];
  accounts: string[];
  labels: string[];
  contracts: string[];
  timeRange: UsageBreakdownTimeRange;
};

export const DEFAULT_FILTER_VALUES: UsageBreakdownFilterValues = {
  clusters: [],
  accounts: [],
  labels: [],
  contracts: [],
  timeRange: '3',
};

const usageBreakdownSearchParamsSchema = z.object({
  // Comma-separated cluster/account/contract ids and `key:value` labels
  clusters: z.string().optional().catch(undefined),
  accounts: z.string().optional().catch(undefined),
  labels: z.string().optional().catch(undefined),
  contracts: z.string().optional().catch(undefined),
  timeRange: z.enum(TIME_RANGE_VALUES).optional().catch(undefined),
});

export type UsageBreakdownSearchParams = z.infer<typeof usageBreakdownSearchParamsSchema>;

/**
 * Parses in loose mode so search params unrelated to the usage breakdown filters
 * (e.g. Stripe redirect params) survive route validation. The declared type stays free of the
 * `Record<string, unknown>` index signature that would otherwise leak: TanStack Router merges
 * every route's search type into the router-wide search types, and the index signature would
 * break type inference on unrelated routes. Since zod 4 a loose object infers without it.
 */
// oxlint-disable-next-line eslint-js/no-restricted-syntax
export function usageBreakdownSearchSchema(search: Record<string, unknown>): UsageBreakdownSearchParams {
  return usageBreakdownSearchParamsSchema.loose().parse(search);
}

function parseIdList(value: string | undefined) {
  return value ? value.split(',').filter(Boolean) : [];
}

export function searchToFilterValues(
  search: UsageBreakdownSearchParams,
  hasChildAccounts: boolean,
): UsageBreakdownFilterValues {
  const { timeRange } = search;

  return {
    clusters: parseIdList(search.clusters),
    // The accounts filter only applies to multi-account setups, the time range filter to single-account ones
    accounts: hasChildAccounts ? parseIdList(search.accounts) : [],
    labels: parseIdList(search.labels),
    contracts: parseIdList(search.contracts),
    timeRange: timeRange ?? DEFAULT_FILTER_VALUES.timeRange,
  };
}

/**
 * Drops cluster/account/contract ids and labels that are not present in the select options (e.g.
 * stale or mistyped values in the URL), which would otherwise count as applied filters the
 * selects cannot display.
 */
export function sanitizeFilterValues(
  filters: UsageBreakdownFilterValues,
  clusterOptions: { value: string }[],
  accountOptions: { value: string }[],
  labelOptions: { value: string }[],
  contractOptions: { value: string }[],
): UsageBreakdownFilterValues {
  const clusterIds = new Set(clusterOptions.map((option) => option.value));
  const accountIds = new Set(accountOptions.map((option) => option.value));
  const labels = new Set(labelOptions.map((option) => option.value));
  const contractIds = new Set(contractOptions.map((option) => option.value));
  return {
    ...filters,
    clusters: filters.clusters.filter((id) => clusterIds.has(id)),
    accounts: filters.accounts.filter((id) => accountIds.has(id)),
    labels: filters.labels.filter((label) => labels.has(label)),
    contracts: filters.contracts.filter((id) => contractIds.has(id)),
  };
}

export function filterValuesToSearch(
  filters: UsageBreakdownFilterValues,
  hasChildAccounts: boolean,
): UsageBreakdownSearchParams {
  return {
    clusters: filters.clusters.length > 0 ? filters.clusters.join(',') : undefined,
    accounts: hasChildAccounts && filters.accounts.length > 0 ? filters.accounts.join(',') : undefined,
    labels: filters.labels.length > 0 ? filters.labels.join(',') : undefined,
    contracts: filters.contracts.length > 0 ? filters.contracts.join(',') : undefined,
    // Default values are dropped to keep the URL clean
    timeRange:
      !hasChildAccounts && filters.timeRange !== DEFAULT_FILTER_VALUES.timeRange ? filters.timeRange : undefined,
  };
}
